import { FilePlus, FileText, MessageSquare, Upload, X } from "lucide-react";
import { AgentMark } from "./AgentMark.tsx";
import type { AgentStatus, DemoTarget, ReplicaState } from "@/product-stage/demo/types.ts";
import { sceneForAgent } from "@/product-stage/demo/world.ts";

function statusRank(status: AgentStatus): number {
  if (status === "working") return 0;
  if (status === "present") return 1;
  return 2;
}

export function HudReplica({
  state,
  onSelect
}: {
  state: ReplicaState;
  onSelect(target: DemoTarget): void;
}) {
  const visibleAgents =
    state.view === "direct"
      ? state.agents.filter((agent) => agent.id === state.active.agentId)
      : state.agents;
  const working = visibleAgents.filter((agent) => agent.status === "working").length;
  const ranked = [...visibleAgents].sort(
    (left, right) => statusRank(left.status) - statusRank(right.status)
  );
  const showThreads = state.view !== "direct";
  const showFiles = state.view !== "direct";
  return (
    <aside className="workspace-hud">
      <header className="workspace-hud-head">
        <div className="workspace-hud-title">
          <strong>工作状态</strong>
          <small>{state.hud.summary}</small>
        </div>
        <button type="button" className="icon-round hud-close" aria-label="收起工作状态栏" tabIndex={-1}>
          <X />
        </button>
      </header>
      <div className="workspace-hud-body">
        <section className="hud-section">
          <div className="hud-section-head">
            <strong>智能体</strong>
            <span className="hud-count">{ranked.length}</span>
            {working > 0 ? <em>工作中 {working}</em> : null}
          </div>
          <div className="hud-section-body">
            {ranked.length === 0 ? <p className="hud-empty">这个对话还没有智能体</p> : null}
            {ranked.length > 0 ? (
              <div className="hud-agent-group">
                {ranked.map((agent) => (
                  <button
                    key={agent.id}
                    type="button"
                    className="hud-agent-face"
                    data-working={agent.status === "working" || undefined}
                    data-selected={state.active.agentId === agent.id}
                    data-idle={agent.status === "idle" ? "true" : undefined}
                    aria-label={
                      agent.status === "working" ? `${agent.name} · 工作中` : agent.name
                    }
                    onClick={() => onSelect({ kind: "scene", sceneId: sceneForAgent(agent.id) })}
                  >
                    <AgentMark agent={agent} variant="hud" animated={agent.status === "working"} />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </section>

        {showFiles ? (
          <section className="hud-section">
            <div className="hud-section-head">
              <strong>文件动态</strong>
              <span className="hud-count">{state.hud.fileActivities.length}</span>
              <button
                type="button"
                className="hud-more"
                onClick={() => onSelect({ kind: "scene", sceneId: "project-files" })}
              >
                查看
              </button>
            </div>
            <div className="hud-section-body">
              {state.hud.fileActivities.length === 0 ? (
                <p className="hud-empty">最近的文件变动会显示在这里</p>
              ) : (
                state.hud.fileActivities.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="hud-row"
                    onClick={() => onSelect({ kind: "scene", sceneId: "project-files" })}
                  >
                    <span className="hud-mark" data-kind={item.operation} aria-hidden>
                      {item.operation === "uploaded" ? (
                        <Upload />
                      ) : item.operation === "created" ? (
                        <FilePlus />
                      ) : (
                        <FileText />
                      )}
                    </span>
                    <span className="hud-row-copy">
                      <strong>{item.name}</strong>
                      <small>{item.detail}</small>
                    </span>
                    <time>{item.time}</time>
                  </button>
                ))
              )}
            </div>
          </section>
        ) : (
          <section className="hud-section">
            <div className="hud-section-head">
              <strong>文件动态</strong>
              <span className="hud-count">0</span>
            </div>
            <p className="hud-empty">最近的文件变动会显示在这里</p>
          </section>
        )}

        {showThreads ? (
          <section className="hud-section" data-grow="true">
            <div className="hud-section-head">
              <strong>近期线程</strong>
              <button
                type="button"
                className="hud-more"
                onClick={() => onSelect({ kind: "tab", tab: "threads" })}
              >
                查看
              </button>
            </div>
            <div className="hud-section-body">
              {state.hud.recentThreads.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="hud-row"
                  onClick={() => onSelect({ kind: "scene", sceneId: item.sceneId })}
                >
                  <span className="hud-mark" data-kind="thread" aria-hidden>
                    <MessageSquare />
                  </span>
                  <span className="hud-row-copy">
                    <strong>{item.title}</strong>
                    <small>{item.meta}</small>
                  </span>
                </button>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </aside>
  );
}
